<script setup>
// O follow-up desta conversa, para quem está atendendo.
//
// Separado do painel de depuração de propósito, e não por organização: aquele é de
// administrador, porque carrega URL de tool e parâmetros de upstream. Este precisa estar na
// mão de quem atende — é quem recebe o "não me manda mais mensagem", e um interruptor que só
// o administrador alcança é um interruptor que ninguém aciona na hora que importa.
//
// Carrega no CLIQUE, como o painel de rastro: a maioria das conversas não é de agente nenhum,
// e uma ida ao runtime por conversa aberta seria custo permanente de uma tela pouco usada.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  fraseDaDecisao,
  estadoDaDecisao,
} from 'dashboard/helper/followupTrace';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const dialogRef = ref(null);
const carregando = ref(false);
const salvando = ref(false);
const erro = ref('');
const agente = ref(null);
const decisoes = ref([]);
const desligado = ref(false);
const motivoDesligado = ref('');
const tentativas = ref(0);

const semAgente = computed(
  () => !carregando.value && !erro.value && !agente.value
);

async function carregar() {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await ConversationApi.agentFollowup(props.conversationId);
    agente.value = data.agent;
    decisoes.value = data.decisoes || [];
    desligado.value = Boolean(data.opted_out);
    motivoDesligado.value = data.opted_out_reason || '';
    tentativas.value = data.tentativas || 0;
  } catch (e) {
    // "runtime fora do ar" e "esta conversa não é de agente" não podem virar o mesmo cinza:
    // um é problema da plataforma, o outro é o caso normal.
    erro.value =
      e?.response?.data?.error === 'runtime_unreachable'
        ? t('CONVERSATION.AGENT_FOLLOWUP.RUNTIME_DOWN')
        : t('CONVERSATION.AGENT_FOLLOWUP.LOAD_ERROR');
  } finally {
    carregando.value = false;
  }
}

function abrir() {
  dialogRef.value?.open();
  carregar();
}

async function alternar() {
  salvando.value = true;
  try {
    const { data } = await ConversationApi.setAgentFollowup(
      props.conversationId,
      {
        optedOut: !desligado.value,
      }
    );
    desligado.value = Boolean(data.opted_out);
    useAlert(
      desligado.value
        ? t('CONVERSATION.AGENT_FOLLOWUP.TURNED_OFF')
        : t('CONVERSATION.AGENT_FOLLOWUP.TURNED_ON')
    );
    // Relê: o motivo passa a existir (ou some) do lado do servidor, com quem desligou.
    await carregar();
  } catch (e) {
    useAlert(t('CONVERSATION.AGENT_FOLLOWUP.SAVE_ERROR'));
  } finally {
    salvando.value = false;
  }
}

// O custo sai daqui e não do template porque o linter de i18n barra texto solto — e ele tem
// razão: "US$" é a única parte que um dia vira outra moeda.
function custoEmTexto(valor) {
  return ` · US$ ${valor.toFixed(5)}`;
}

function horaDe(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}
</script>

<template>
  <span>
    <Button
      v-tooltip="t('CONVERSATION.AGENT_FOLLOWUP.TITLE')"
      icon="i-lucide-alarm-clock"
      slate
      faded
      sm
      @click="abrir"
    />

    <Dialog
      ref="dialogRef"
      :title="t('CONVERSATION.AGENT_FOLLOWUP.TITLE')"
      :show-cancel-button="false"
      :show-confirm-button="false"
      width="lg"
    >
      <div v-if="carregando" class="py-6 text-sm text-center text-n-slate-11">
        {{ t('CONVERSATION.AGENT_FOLLOWUP.LOADING') }}
      </div>

      <p
        v-else-if="erro"
        class="p-3 text-sm rounded-lg bg-n-ruby-2 text-n-ruby-11"
      >
        {{ erro }}
      </p>

      <p v-else-if="semAgente" class="py-6 text-sm text-center text-n-slate-11">
        {{ t('CONVERSATION.AGENT_FOLLOWUP.NO_AGENT') }}
      </p>

      <div v-else class="flex flex-col gap-4">
        <!-- O interruptor primeiro, e âmbar quando desligado: é a ação que alguém veio
             fazer, e não uma configuração para ler depois do relatório. -->
        <div
          class="flex flex-wrap items-center justify-between gap-3 p-3 border rounded-xl"
          :class="desligado ? 'border-n-amber-6 bg-n-amber-2' : 'border-n-weak'"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{
                desligado
                  ? t('CONVERSATION.AGENT_FOLLOWUP.OFF_TITLE')
                  : t('CONVERSATION.AGENT_FOLLOWUP.ON_TITLE', {
                      name: agente.name,
                    })
              }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{
                desligado
                  ? motivoDesligado || t('CONVERSATION.AGENT_FOLLOWUP.OFF_HINT')
                  : t('CONVERSATION.AGENT_FOLLOWUP.ON_HINT')
              }}
            </p>
          </div>
          <Button
            :label="
              desligado
                ? t('CONVERSATION.AGENT_FOLLOWUP.TURN_ON')
                : t('CONVERSATION.AGENT_FOLLOWUP.TURN_OFF')
            "
            :is-loading="salvando"
            :ruby="!desligado"
            :slate="desligado"
            sm
            class="shrink-0"
            @click="alternar"
          />
        </div>

        <div>
          <h4
            class="mb-2 text-xs font-semibold tracking-wide uppercase text-n-slate-11"
          >
            {{ t('CONVERSATION.AGENT_FOLLOWUP.HISTORY') }}
          </h4>

          <p
            v-if="!decisoes.length"
            class="p-3 text-xs border border-dashed rounded-lg border-n-weak text-n-slate-10"
          >
            {{ t('CONVERSATION.AGENT_FOLLOWUP.NO_DECISIONS') }}
          </p>

          <ul v-else class="flex flex-col gap-1.5">
            <li
              v-for="(d, i) in decisoes"
              :key="i"
              class="flex items-start gap-2.5 p-2.5 border rounded-lg border-n-weak"
            >
              <i
                class="mt-0.5 text-sm shrink-0"
                :class="{
                  'i-lucide-send text-n-teal-11':
                    estadoDaDecisao(d) === 'enviado',
                  'i-lucide-check text-n-slate-11':
                    estadoDaDecisao(d) === 'autorizado',
                  'i-lucide-minus-circle text-n-slate-10':
                    estadoDaDecisao(d) === 'barrado',
                }"
              />
              <div class="min-w-0">
                <p class="text-sm text-n-slate-12">
                  {{ fraseDaDecisao(d.fonte) }}
                </p>
                <p
                  v-if="d.motivo"
                  class="mt-0.5 text-xs text-n-slate-11 break-words"
                >
                  {{ d.motivo }}
                </p>
                <p class="mt-1 text-[11px] text-n-slate-10">
                  {{ horaDe(d.em) }}
                  <span v-if="estadoDaDecisao(d) === 'autorizado'">
                    · {{ t('CONVERSATION.AGENT_FOLLOWUP.NOT_SENT') }}
                  </span>
                  <span v-if="d.custo_usd">{{
                    custoEmTexto(d.custo_usd)
                  }}</span>
                </p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </Dialog>
  </span>
</template>
